import React, { Component, ErrorInfo, ReactNode } from 'react';
import { logger, ErrorCode } from 'utils/logger';

interface ErrorBoundaryProps {
	children: ReactNode;
	fallback?: ReactNode;
	onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
	hasError: boolean;
	error: Error | null;
}

/**
 * Error Boundary component to catch and log React rendering errors.
 * Prevents the entire app from crashing due to component errors.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
	constructor(props: ErrorBoundaryProps) {
		super(props);
		this.state = {
			hasError: false,
			error: null
		};
	}

	static getDerivedStateFromError(error: Error): ErrorBoundaryState {
		// Update state so the next render will show the fallback UI
		return {
			hasError: true,
			error
		};
	}

	componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
		// Log error with component stack
		logger.error(
			'React component error caught by Error Boundary',
			error,
			{
				component: 'ErrorBoundary',
				componentStack: errorInfo.componentStack,
				// Add current URL context if available
				...(typeof window !== 'undefined' && {
					path: window.location.pathname,
					url: window.location.href
				})
			},
			ErrorCode.PAGE_RENDER_ERROR
		);

		// Call custom error handler if provided
		if (this.props.onError) {
			this.props.onError(error, errorInfo);
		}
	}

	render(): ReactNode {
		if (this.state.hasError) {
			// Render custom fallback UI if provided
			if (this.props.fallback) {
				return this.props.fallback;
			}

			// Default fallback UI
			return (
				<div
					style={{
						padding: '2rem',
						maxWidth: '800px',
						margin: '0 auto',
						textAlign: 'center'
					}}
				>
					<h1 style={{ fontSize: '2rem', marginBottom: '1rem', color: '#dc2626' }}>Er is iets misgegaan</h1>
					<p style={{ marginBottom: '1.5rem', color: '#374151' }}>
						We hebben een fout ondervonden bij het laden van deze pagina. Het probleem is geregistreerd en we werken aan
						een oplossing.
					</p>
					<button
						onClick={() => {
							this.setState({ hasError: false, error: null });
							window.location.reload();
						}}
						style={{
							padding: '0.75rem 1.5rem',
							backgroundColor: '#2563eb',
							color: 'white',
							border: 'none',
							borderRadius: '0.375rem',
							cursor: 'pointer',
							fontSize: '1rem',
							fontWeight: '500'
						}}
					>
						Pagina opnieuw laden
					</button>
					{process.env.NODE_ENV === 'development' && this.state.error && (
						<details style={{ marginTop: '2rem', textAlign: 'left' }}>
							<summary style={{ cursor: 'pointer', fontWeight: 'bold', marginBottom: '0.5rem' }}>
								Foutdetails (alleen zichtbaar in development)
							</summary>
							<pre
								style={{
									backgroundColor: '#f3f4f6',
									padding: '1rem',
									borderRadius: '0.375rem',
									overflow: 'auto',
									fontSize: '0.875rem'
								}}
							>
								{this.state.error.stack}
							</pre>
						</details>
					)}
				</div>
			);
		}

		return this.props.children;
	}
}

export default ErrorBoundary;
